import { Typography } from '@mui/material';
import { type LatLngTuple } from 'leaflet';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import SpinnerLoader from '../Loaders/SpinnerLoader';

import './GeoMap.css';
import type { CountryCoordinates, GeoMapProps, MarkerValue } from '@/types/monitoringTypes';

const countryLookup: CountryCoordinates = {
  SK: [48.669, 19.699],
  CZ: [49.817, 15.472],
  RO: [45.943, 24.966],
  HU: [47.162, 19.503],
  AT: [47.516, 14.55],
  ME: [42.708, 19.374],
  PL: [51.919, 19.145],
  HR: [45.1, 15.2],
  MK: [41.608, 21.745],
  DE: [51.165, 10.451],
  US: [37.09, -95.712],
};

const GeoMap = ({ panelInfo, mapZoom, styles, isLoading, panelData }: GeoMapProps) => {
  const getPanelValues = (): MarkerValue[] | null => {
    if (panelData && Object.keys(panelData).length === 0) {
      return null;
    }
    const { refId } = panelInfo.targets?.find(target => !target.hide) ?? {};
    if (!refId) {
      return null;
    }

    const fieldLookup = panelInfo.transformations?.find(t => t.id === 'organize')?.options
      ?.renameByName;

    const frameData = panelData?.results[refId]?.frames[0];
    if (!frameData?.data.values) {
      return null;
    }

    const frames = {
      values: frameData.data.values,
      fieldNames: frameData.schema?.fields.map(field => fieldLookup?.[field.name] ?? field.name),
    };

    if (frames.values[0]) {
      const markers: MarkerValue[] = (frames.values[0] as string[]).map((country, index) => {
        const fieldName0 = frames.fieldNames?.[0] ?? 'Field 1';
        const fieldName1 = frames.fieldNames?.[1] ?? 'Field 2';

        return {
          marker: {
            country,
            position: (countryLookup[country] ?? []) as [number, number],
            value: (frames.values[1] as number[])[index],
          },
          schema: [
            {
              fieldName: fieldName0,
              value: country,
            },
            {
              fieldName: fieldName1,
              value: (frames.values[1] as number[])[index],
            },
          ],
        };
      });
      return markers;
    }

    return null;
  };

  const values = getPanelValues();
  const mapCenter: LatLngTuple = [48.669, 19.699];

  return (
    <SpinnerLoader
      isLoading={isLoading}
      height={styles?.height ?? ''}
      width={styles?.width ?? ''}
      labelText='Loading map data...'
    >
      {values ? (
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          minZoom={3}
          attributionControl={false}
          style={{
            height: styles?.height ?? '100%',
            width: styles?.width ?? '100%',
          }}
        >
          <TileLayer url='https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png' />
          {values.map(value => (
            <div key={value.marker.country}>
              <CircleMarker
                center={value.marker.position}
                radius={value.marker.value ? value.marker.value / 10 + 10 : 10}
                color='#E20074'
                weight={1}
                fillOpacity={0.2}
              >
                <Tooltip
                  direction='auto'
                  className='dark-tooltip'
                >
                  <table>
                    <thead>
                      <tr>
                        <th>
                          <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                            {panelInfo.title}
                          </Typography>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {value.schema.map(field => (
                        <tr key={`${field.fieldName}+${value.marker.country}`}>
                          <td>
                            <Typography
                              sx={{ fontWeight: 'bold' }}
                            >{`${field.fieldName}:`}</Typography>
                          </td>
                          <td align='right'>
                            <Typography>{field.value ?? 'N/A'}</Typography>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Tooltip>
              </CircleMarker>

              <CircleMarker
                center={value.marker.position}
                radius={0}
                opacity={0}
              >
                <Tooltip
                  direction='center'
                  permanent
                  className='transparent-tooltip'
                >
                  <Typography
                    align='center'
                    color='primary'
                  >
                    {value.marker.value}
                  </Typography>
                </Tooltip>
              </CircleMarker>
            </div>
          ))}
        </MapContainer>
      ) : (
        <div
          style={{
            height: styles?.height,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant='h4'
            color='primary'
            align='center'
          >
            {!isLoading && 'No Data'}
          </Typography>
        </div>
      )}
    </SpinnerLoader>
  );
};

export default GeoMap;
